// CSR-1993 — System Settings "Stale Sync": the per-client answer to "who has
// the carrier sweep stopped refreshing?", complementing CSR-1343's global
// "sync is down" banner and CSR-1719's book-level Datadog gauges.
//
// The staleness definition lives in DC (CSR-1992) — this renders it, it does
// not recompute it, so the list and the alert can never disagree.
import { useState } from "react";
import { useLocation } from "wouter";
import { AlarmClockOff, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ColumnMultiSelect,
  type ColumnOption,
} from "./ColumnMultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStaleSyncPolicies, type StaleSyncPolicyRow } from "@/lib/api";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { AorIcon, aorPolicyTooltip } from "@/components/insured/policies/aor";
import {
  asDcSyncAttemptStatus,
  type DcSyncAttemptStatus,
} from "@shared/sync-attempt";
import {
  TABLE_CLASS,
  THEAD_CLASS,
  TH_CLASS,
  TBODY_CLASS,
  TR_CLASS,
  TD_CLASS,
} from "@/lib/table-styles";

/** Mirrors `FLAG_STALE_SYNC_VIEW` in `server/services/dcStaleSyncReads.ts`.
 *  The client cannot import a server module, so the literal is repeated — a
 *  test on each side pins it, because a silent mismatch would simply hide the
 *  tab forever with nothing failing anywhere. */
export const FLAG_STALE_SYNC_VIEW = "feature_flag_stale_sync_view";

/** DC clamps pageSize at 100. One request therefore covers the whole
 *  in-force set (~92 rows in production), which is what makes the CSR-2094
 *  client-side column filters honest — see `wholeSetLoaded` below. */
export const STALE_SYNC_PAGE_SIZE = 100;

/** CSR-2194 — prioritize policies that have not been checked in more than one
 *  day, matching the proactive `dc.book.stale_over_24h` alert population. */
export const DEFAULT_STALE_FOR_HOURS = 24;

/** CSR-2134 — the widest position of the time selector, and the only one that
 *  can reach a policy the carrier does not recognize. Such a policy is rejected
 *  on every sweep, so it is always freshly checked and no numeric threshold
 *  ever includes it. DC drops the freshness predicate entirely for this value. */
const ALL_HOURS = "all";

const THRESHOLD_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: ALL_HOURS, label: "All policies" },
  { value: "24", label: "Older than 24 hours" },
  { value: "48", label: "Older than 48 hours" },
  { value: "72", label: "Older than 72 hours" },
  { value: "168", label: "Older than 7 days" },
  { value: "336", label: "Older than 14 days" },
  { value: "720", label: "Older than 30 days" },
];

/** CSR-2134 — "the carrier has never once returned data for this number" is the
 *  strongest phantom-policy signal DC can give us, so it gets its own control
 *  rather than hiding as one value among many. */
const SUCCESS_ANY = "any";
const SUCCESS_NEVER = "never";

const SUCCESS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: SUCCESS_ANY, label: "Any success history" },
  { value: SUCCESS_NEVER, label: "Never had a success" },
];

const CREATED_ANY = "any";

const CREATED_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: CREATED_ANY, label: "Any age" },
  { value: "7", label: "Created in last 7 days" },
  { value: "30", label: "Created in last 30 days" },
  { value: "90", label: "Created in last 90 days" },
];

/** A streak of 0 matches every row, so it is not a filter. The route rejects it
 *  too — this keeps the UI from composing a request it knows is meaningless. */
const MIN_STREAK = 1;

/** Long enough that typing "12" does not fire a request for "1" first. */
const STREAK_DEBOUNCE_MS = 400;

/** The streak the user typed, or null if it is not a usable one. Digits only —
 *  `Number.parseInt` alone would read "12abc" as 12 and silently ask a question
 *  nobody typed. One parser serves both the request and the field validation so
 *  the two can never disagree about what counts as valid. */
export function parseStreak(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= MIN_STREAK ? value : null;
}

/** Radix Select rejects an empty item value, so "all" is the sentinel the UI
 *  uses and "" is what goes on the wire (DC reads empty as both books). */
const ALL_CARRIERS = "all";

/** CSR-2069 — the default view. Without it the tab opens on ~3300 rows in
 *  production of which ~92 are actionable, and the signal is unfindable.
 *  DC applies the narrowing (CSR-2090) because it paginates server-side. */
const IN_FORCE = "in-force";
const ALL_STATUSES = "all";

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: IN_FORCE, label: "In force only" },
  { value: ALL_STATUSES, label: "All statuses" },
];

const CARRIER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: ALL_CARRIERS, label: "All carriers" },
  { value: "geico", label: "GEICO" },
  { value: "progressive", label: "Progressive" },
];

/** Exhaustive over DC's attempt vocabulary — `Record<DcSyncAttemptStatus, …>`
 *  means adding a status upstream fails to compile until it is labeled here.
 *  That is deliberate: CSR-1976 added `no_change` precisely because consumers
 *  were treating a healthy outcome as an error, and a permissive default
 *  branch is how that bug comes back. */
const OUTCOME_LABELS: Record<
  DcSyncAttemptStatus,
  { label: string; className: string }
> = {
  completed: { label: "Confirmed", className: "text-emerald-700" },
  no_change: { label: "No change", className: "text-emerald-700" },
  not_found: { label: "Not found", className: "text-rose-600" },
  failed: { label: "Failed", className: "text-rose-600" },
  stopped: { label: "Stopped", className: "text-rose-600" },
  interrupted: { label: "Interrupted", className: "text-rose-600" },
  waiting: { label: "Waiting", className: "text-muted-foreground" },
  running: { label: "Running", className: "text-muted-foreground" },
};

const UNATTRIBUTED_ATTEMPT_TITLE =
  "No sync attempt could be attributed to this policy term";

/** "Never synced" is a statement about `lastSync` and nothing else. */
export function formatLastRefresh(row: StaleSyncPolicyRow): string {
  if (row.lastSync == null) return "Never synced";
  const hours = row.hoursSinceLastSync;
  if (hours == null) return formatDate(row.lastSync);
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function LastRefreshCell({ row }: { row: StaleSyncPolicyRow }) {
  const text = formatLastRefresh(row);
  return (
    <span title={row.lastSync ? formatDate(row.lastSync) : undefined}>
      {text}
    </span>
  );
}

/** DC advances `last_sync` even on a `not_found` (it throttles the scrape
 *  queue that way), so the timestamp alone is not proof the carrier confirmed
 *  anything. This column carries that second, independent fact — and it never
 *  speaks for the timestamp: a missing attempt renders "--", never
 *  "never synced". */
function LastOutcomeCell({ row }: { row: StaleSyncPolicyRow }) {
  const attempt = row.latestSyncAttempt;
  if (!attempt) {
    return (
      <span className="text-muted-foreground" title={UNATTRIBUTED_ATTEMPT_TITLE}>
        --
      </span>
    );
  }
  const known = asDcSyncAttemptStatus(attempt.status);
  // An unrecognized status is forwarded, not nulled: collapsing it would make
  // the row indistinguishable from "no attributable attempt", a different fact.
  // `outcomeLabel` derives the same text the CSR-2094 filter matches on, so the
  // column and its filter cannot drift.
  const display = known
    ? OUTCOME_LABELS[known]
    : { label: outcomeLabel(row), className: "text-muted-foreground" };
  return (
    <span
      className={display.className}
      title={attempt.errorMessage ?? undefined}
    >
      {display.label}
    </span>
  );
}

/** CSR-2134 — when the DC row was created, so a mistake made last week is
 *  distinguishable from debris that has been rotting for a year. */
export function formatCreated(row: StaleSyncPolicyRow): string {
  return row.createdAt ? formatDate(row.createdAt) : "--";
}

/** CSR-2134 — `0` is a real answer ("the last attempt succeeded") and must not
 *  render as the "DC told us nothing" placeholder, so this tests for null and
 *  never for falsiness. */
export function formatNotFoundStreak(row: StaleSyncPolicyRow): string {
  return row.notFoundStreak == null ? "--" : String(row.notFoundStreak);
}

/** CSR-2134 — the three-way distinction that carries this whole ticket:
 *
 *  - `undefined` → DC never reported the field (a deploy predating CSR-2133).
 *     Renders "--": we were not told, which is not an accusation.
 *  - `null` → DC reported that the carrier has NEVER returned data for this
 *     number. Renders "Never" — the strongest phantom-policy signal there is.
 *  - a date → the carrier last confirmed the policy then.
 *
 *  Collapsing the first two would print "Never" against every row in the book
 *  the moment AMS ran ahead of DC, which is exactly the kind of confident
 *  falsehood this tab exists to prevent. */
export function formatLastSuccess(row: StaleSyncPolicyRow): string {
  if (row.lastSuccessAt === undefined) return "--";
  if (row.lastSuccessAt === null) return "Never";
  return formatDate(row.lastSuccessAt);
}

const NEVER_SUCCEEDED_TITLE =
  "The carrier has never returned data for this policy number";

const UNREPORTED_HISTORY_TITLE =
  "This Data Center deployment does not report success history";

function LastSuccessCell({ row }: { row: StaleSyncPolicyRow }) {
  const text = formatLastSuccess(row);
  if (row.lastSuccessAt === undefined) {
    return (
      <span className="text-muted-foreground" title={UNREPORTED_HISTORY_TITLE}>
        {text}
      </span>
    );
  }
  if (row.lastSuccessAt === null) {
    return (
      <span className="text-rose-600 font-medium" title={NEVER_SUCCEEDED_TITLE}>
        {text}
      </span>
    );
  }
  return <span title={formatDate(row.lastSuccessAt)}>{text}</span>;
}

/** A run of consecutive rejections. Any run at all is worth the eye; a long one
 *  is the phantom-policy signal. CSR-1879 is why the run — and not a plain
 *  lifetime count — is what gets shown: Progressive returns false `not_found`
 *  for hours at a stretch, so only "consecutive since the last success" can
 *  tell a genuine phantom from an established policy having a bad day. */
function NotFoundStreakCell({ row }: { row: StaleSyncPolicyRow }) {
  const streak = row.notFoundStreak;
  const emphasized = streak != null && streak > 0;
  return (
    <span className={emphasized ? "text-rose-600" : "text-muted-foreground"}>
      {formatNotFoundStreak(row)}
    </span>
  );
}

const COLUMN_COUNT = 9;

/** CSR-2094 — one filter per column, matched against the text the column
 *  actually renders. Filtering the raw field instead would surprise whoever
 *  reads the screen: "Last outcome" shows "Not found", never DC's `not_found`. */
const COLUMN_FILTERS: ReadonlyArray<{
  key: string;
  testId: string;
  label: string;
  /** CSR-2095 — `pick` for closed vocabularies (Status, Last outcome), where
   *  triage needs OR across several values and a typo in a text box returns a
   *  silent zero. `type` for open values you narrow by fragment. */
  kind: "type" | "pick";
  text: (row: StaleSyncPolicyRow) => string;
}> = [
  {
    key: "client",
    kind: "type",
    testId: "filter-client",
    label: "Filter by client",
    text: (row) => row.insuredName ?? row.customerName ?? "",
  },
  {
    key: "policy",
    kind: "type",
    testId: "filter-policy",
    label: "Filter by policy",
    text: (row) => row.policyNumber ?? "",
  },
  {
    key: "carrier",
    kind: "type",
    testId: "filter-carrier",
    label: "Filter by carrier",
    text: (row) => row.carrier ?? "",
  },
  {
    key: "status",
    kind: "pick",
    testId: "filter-status",
    label: "Filter by status",
    text: (row) => row.status ?? "",
  },
  {
    key: "created",
    kind: "type",
    testId: "filter-created",
    label: "Filter by created date",
    text: (row) => formatCreated(row),
  },
  {
    key: "refresh",
    kind: "type",
    testId: "filter-refresh",
    label: "Filter by last refresh",
    text: (row) => formatLastRefresh(row),
  },
  {
    key: "outcome",
    kind: "pick",
    testId: "filter-outcome",
    label: "Filter by last outcome",
    text: (row) => outcomeLabel(row),
  },
  {
    // `pick`, not `type`: a substring match on a number is a trap — "2" would
    // also match a streak of 12 — and the distinct values in a loaded page are
    // few enough to choose from directly.
    key: "streak",
    kind: "pick",
    testId: "filter-streak",
    label: "Filter by not-found streak",
    text: (row) => formatNotFoundStreak(row),
  },
  {
    // `pick` so "Never" is one click. That is the triage move this tab exists
    // for, and typing it into a text box would be a silent zero on a typo.
    key: "success",
    kind: "pick",
    testId: "filter-success",
    label: "Filter by last success",
    text: (row) => formatLastSuccess(row),
  },
];

/** The label rendered in "Last outcome", extracted so the filter matches what
 *  the eye sees. `--` covers both "no attempt" and "not attributable". */
export function outcomeLabel(row: StaleSyncPolicyRow): string {
  const attempt = row.latestSyncAttempt;
  if (!attempt) return "--";
  const known = asDcSyncAttemptStatus(attempt.status);
  return known ? OUTCOME_LABELS[known].label : (attempt.status ?? "--");
}

/** AND across columns, OR within a multi-select. An empty text box and an
 *  empty selection both mean "no filter on this column". */
function matchesFilters(
  row: StaleSyncPolicyRow,
  typed: Record<string, string>,
  picked: Record<string, string[]>,
): boolean {
  return COLUMN_FILTERS.every((column) => {
    const value = column.text(row);
    if (column.kind === "pick") {
      const chosen = picked[column.key] ?? [];
      return chosen.length === 0 || chosen.includes(value);
    }
    const needle = typed[column.key]?.trim().toLowerCase();
    if (!needle) return true;
    return value.toLowerCase().includes(needle);
  });
}

/** Distinct values present in the loaded rows, with counts, most common first.
 *  Only what is actually there — offering a value that matches nothing would
 *  send the reader looking for rows that do not exist. */
function optionsFor(
  column: (typeof COLUMN_FILTERS)[number],
  rows: StaleSyncPolicyRow[],
): ColumnOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value = column.text(row);
    if (!value) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return Array.from(counts, ([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

// CSR-2327 — `showAor` is the AOR experience flag, read by System Settings.
export function StaleSyncTab({ showAor = false }: { showAor?: boolean } = {}) {
  const [, navigate] = useLocation();
  const [staleForHours, setStaleForHours] = useState<number | typeof ALL_HOURS>(
    DEFAULT_STALE_FOR_HOURS,
  );
  const [carrier, setCarrier] = useState(ALL_CARRIERS);
  const [statusScope, setStatusScope] = useState(IN_FORCE);
  const [successScope, setSuccessScope] = useState(SUCCESS_ANY);
  const [createdScope, setCreatedScope] = useState(CREATED_ANY);
  const [streakInput, setStreakInput] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [pickedValues, setPickedValues] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);

  // The request is debounced so typing "12" doesn't first ask DC for every row
  // with a streak of 1. Validity, however, reads what the user has typed right
  // now — judging it by the debounced value would flash the field red while a
  // perfectly good entry was still settling.
  const debouncedStreak = useDebouncedValue(streakInput, STREAK_DEBOUNCE_MS);
  const minNotFoundStreak = parseStreak(debouncedStreak);
  const streakInvalid =
    streakInput.trim() !== "" && parseStreak(streakInput) === null;

  const { data, isLoading, isError, isPlaceholderData } = useStaleSyncPolicies({
    staleForHours,
    carrier: carrier === ALL_CARRIERS ? "" : carrier,
    inForceStatusOnly: statusScope === IN_FORCE,
    neverSucceeded: successScope === SUCCESS_NEVER,
    minNotFoundStreak,
    createdWithinDays:
      createdScope === CREATED_ANY ? null : Number(createdScope),
    page,
    pageSize: STALE_SYNC_PAGE_SIZE,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / STALE_SYNC_PAGE_SIZE));

  // Column filters run in the browser, so they are only truthful while every
  // matching row is already in memory. The hook fetches the remaining pages
  // when the set is small enough (STALE_SYNC_MAX_CLIENT_ROWS); past that it
  // returns one page and these go disabled, because filtering a partial set
  // would quietly hide matches on pages never fetched. Comparing what we HOLD
  // against the reported total is the honest test — not an assumption about
  // how big the set happens to be today.
  const wholeSetLoaded = rows.length >= total;
  const visibleRows = wholeSetLoaded
    ? rows.filter((row) => matchesFilters(row, filterValues, pickedValues))
    : rows;
  const isFiltering =
    wholeSetLoaded &&
    (Object.values(filterValues).some((v) => v.trim() !== "") ||
      Object.values(pickedValues).some((v) => v.length > 0));
  // Once every row is in hand there is nothing left to page through.
  const showFooter = !wholeSetLoaded && total > STALE_SYNC_PAGE_SIZE;

  // In "All policies" there is no threshold to be beyond, so the stock message
  // would be describing a filter that isn't applied. "Selection" points at the
  // population controls; "filters" is reserved for the per-column row.
  const emptyMessage =
    staleForHours === ALL_HOURS
      ? "No policies match the current selection."
      : "No policies are stale beyond this threshold.";

  // Changing a filter changes the population, so the current page number is
  // meaningless against the new result set.
  function changeThreshold(value: string) {
    setStaleForHours(value === ALL_HOURS ? ALL_HOURS : Number(value));
    setPage(1);
  }

  function changeCarrier(value: string) {
    setCarrier(value);
    setPage(1);
  }

  function changeStatusScope(value: string) {
    setStatusScope(value);
    setPage(1);
  }

  function changeSuccessScope(value: string) {
    setSuccessScope(value);
    setPage(1);
  }

  function changeCreatedScope(value: string) {
    setCreatedScope(value);
    setPage(1);
  }

  function changeStreak(value: string) {
    setStreakInput(value);
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlarmClockOff className="h-4 w-4" />
            Stale Sync
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Policies the carrier sweep has stopped refreshing, plus the ones it
            keeps rejecting. Never-synced policies are listed first. Cancelled,
            expired and already-renewed terms are hidden by default. A policy
            the carrier does not recognize always looks freshly checked, so
            reach it with &ldquo;All policies&rdquo; and the success-history
            controls rather than the time window. Visibility only.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(staleForHours)} onValueChange={changeThreshold}>
            <SelectTrigger
              className="w-[170px] cursor-pointer"
              data-testid="select-stale-threshold"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THRESHOLD_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="cursor-pointer"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusScope} onValueChange={changeStatusScope}>
            <SelectTrigger
              className="w-[150px] cursor-pointer"
              data-testid="select-stale-status"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="cursor-pointer"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={carrier} onValueChange={changeCarrier}>
            <SelectTrigger
              className="w-[160px] cursor-pointer"
              data-testid="select-stale-carrier"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CARRIER_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="cursor-pointer"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* CSR-2134 — these three narrow the POPULATION, server-side, unlike
              the per-column filters under the headers which only sift the rows
              already in the browser. That is what makes them usable at 1,968
              rows: DC applies them inside the paginating query. */}
          <Select value={successScope} onValueChange={changeSuccessScope}>
            <SelectTrigger
              className="w-[190px] cursor-pointer"
              data-testid="select-stale-success"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUCCESS_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="cursor-pointer"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={createdScope} onValueChange={changeCreatedScope}>
            <SelectTrigger
              className="w-[190px] cursor-pointer"
              data-testid="select-stale-created"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CREATED_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="cursor-pointer"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            value={streakInput}
            onChange={(event) => changeStreak(event.target.value)}
            inputMode="numeric"
            aria-label="Minimum not-found streak"
            aria-invalid={streakInvalid}
            placeholder="Streak ≥ …"
            className={cn(
              "h-9 w-[130px]",
              streakInvalid && "border-rose-500 focus-visible:ring-rose-500",
            )}
            data-testid="input-stale-streak"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span data-testid="stale-sync-count">
          {isFiltering
            ? `${visibleRows.length.toLocaleString()} of ${total.toLocaleString()}`
            : `${total.toLocaleString()} ${total === 1 ? "policy" : "policies"}`}
        </span>
        {streakInvalid && (
          <span data-testid="streak-invalid" className="text-rose-600">
            Enter a whole number of {MIN_STREAK} or more.
          </span>
        )}
        {!wholeSetLoaded && (
          <span data-testid="filters-unavailable" className="text-amber-700">
            Column filters need the whole list in view — narrow with the
            controls above first.
          </span>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className={TABLE_CLASS}>
            <thead className={THEAD_CLASS}>
              <tr>
                <th className={TH_CLASS}>Client</th>
                <th className={TH_CLASS}>Policy</th>
                <th className={TH_CLASS}>Carrier</th>
                <th className={TH_CLASS}>Status</th>
                <th className={TH_CLASS}>Created</th>
                <th className={TH_CLASS}>Last refresh</th>
                <th className={TH_CLASS}>Last outcome</th>
                <th className={TH_CLASS}>Not-found streak</th>
                <th className={TH_CLASS}>Last success</th>
              </tr>
              <tr>
                {COLUMN_FILTERS.map((column) => (
                  <th key={column.key} className="px-6 pb-3 font-normal">
                    {column.kind === "pick" ? (
                      <ColumnMultiSelect
                        options={optionsFor(column, rows)}
                        selected={pickedValues[column.key] ?? []}
                        onChange={(values) =>
                          setPickedValues((current) => ({
                            ...current,
                            [column.key]: values,
                          }))
                        }
                        disabled={!wholeSetLoaded}
                        label={column.label}
                        testId={column.testId}
                      />
                    ) : (
                      <Input
                        value={filterValues[column.key] ?? ""}
                        onChange={(event) =>
                          setFilterValues((current) => ({
                            ...current,
                            [column.key]: event.target.value,
                          }))
                        }
                        disabled={!wholeSetLoaded}
                        aria-label={column.label}
                        placeholder="Filter…"
                        className="h-8 text-xs font-normal"
                        data-testid={column.testId}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={TBODY_CLASS}>
              {isLoading && (
                <tr>
                  <td
                    className={cn(TD_CLASS, "text-center text-muted-foreground")}
                    colSpan={COLUMN_COUNT}
                  >
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && isError && (
                <tr>
                  <td
                    className={cn(TD_CLASS, "text-center text-rose-600")}
                    colSpan={COLUMN_COUNT}
                  >
                    Failed to load stale sync policies.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && rows.length > 0 && visibleRows.length === 0 && (
                <tr>
                  <td
                    className={cn(TD_CLASS, "text-center text-muted-foreground")}
                    colSpan={COLUMN_COUNT}
                  >
                    No rows match the current filters.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && rows.length === 0 && (
                <tr>
                  <td
                    className={cn(TD_CLASS, "text-center text-muted-foreground")}
                    colSpan={COLUMN_COUNT}
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {!isLoading &&
                !isError &&
                visibleRows.map((row) => (
                  <tr
                    key={row.policyId ?? row.policyNumber}
                    // Whole-row navigation, like every other list table here.
                    // `/policies/:id` resolves the insured and lands on the
                    // insured page with this policy open, so one click gives
                    // both the client context and the stale term. A row with
                    // no policy id isn't navigable, so it must not look it.
                    className={
                      row.policyId
                        ? TR_CLASS
                        : "hover:bg-muted/30 transition-colors"
                    }
                    onClick={
                      row.policyId
                        ? () => navigate(`/policies/${row.policyId}`)
                        : undefined
                    }
                    data-testid={`row-stale-sync-${row.policyId ?? row.policyNumber}`}
                  >
                    <td className={TD_CLASS}>
                      {row.insuredName ?? row.customerName ?? "--"}
                    </td>
                    <td className={TD_CLASS}>
                      <span className="inline-flex items-center gap-1.5">
                        {row.policyNumber ?? "--"}
                        {showAor && row.aorIdentifiedAt && (
                          <AorIcon
                            tooltip={aorPolicyTooltip(row.aorIdentifiedAt)}
                            testId={`stale-sync-aor-${row.policyNumber}`}
                          />
                        )}
                      </span>
                    </td>
                    <td className={TD_CLASS}>{row.carrier ?? "--"}</td>
                    <td className={TD_CLASS}>{row.status ?? "--"}</td>
                    <td className={TD_CLASS}>{formatCreated(row)}</td>
                    <td className={TD_CLASS}>
                      <LastRefreshCell row={row} />
                    </td>
                    <td className={TD_CLASS}>
                      <LastOutcomeCell row={row} />
                    </td>
                    <td className={TD_CLASS}>
                      <NotFoundStreakCell row={row} />
                    </td>
                    <td className={TD_CLASS}>
                      <LastSuccessCell row={row} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {showFooter && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              {((page - 1) * STALE_SYNC_PAGE_SIZE + 1).toLocaleString()}&ndash;
              {Math.min(page * STALE_SYNC_PAGE_SIZE, total).toLocaleString()} of{" "}
              {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isPlaceholderData}
                data-testid="button-prev-page"
              >
                Previous
              </Button>
              <span className="text-sm px-3">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isPlaceholderData}
                data-testid="button-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
